import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isNil } from 'lodash';
import getRenderable from '../../../lib/getRenderable';
import Collapsible from '../Collapsible';
import classes from './index.module.scss';

/**
 * @deprecated Please use CollapsibleGroup from @fiverr-private/disclosure instead.
 */
class CollapsibleGroup extends React.Component {
    constructor(props) {
        super(props);

        this.panels = [];
        this.expandedPanel = isNil(props.defaultExpandedPanel) ? null : props.defaultExpandedPanel;

        this.updateCurrentPanel = this.updateCurrentPanel.bind(this);
        this.generatePanel = this.generatePanel.bind(this);
    }

    /**
     * Updates the current active panel.
     * If the panel clicked is the one currently open, it will minimize.
     * @param {Number}  panelIndex  The clicked panel's index.
     */
    updateCurrentPanel(panelIndex) {
        const prevExpandedPanel = this.expandedPanel;
        this.expandedPanel = panelIndex;

        if (
            prevExpandedPanel === null ||
            prevExpandedPanel === panelIndex
        ) {
            return;
        }

        this.panels[prevExpandedPanel].minimize();
    }

    /**
     * Generates a single Collapsible panel, enriched with props.
     * @param  {Node}        component A Collapsible panel.
     * @param  {Number}      index     The panel's index.
     * @return {Collapsible}           The enriched Collapsible panel.
     */
    generatePanel(component, index) {
        return getRenderable(component, {
            defaultExpanded: index === this.expandedPanel,
            ref: (component) => this.panels[index] = component,
            key: index,
            className: classnames(
                classes.collapsible,
                component.props.className
            )
        }, {
            onToggle: (toggleState) => {
                this.updateCurrentPanel(index);
                component.props.onToggle(toggleState);
            }
        });
    }

    /**
     * Generates the panel array, enriching the <Collapsible> elements with props.
     * @return {Collapsible[]}
     */
    generatePanels() {
        const { children } = this.props;

        return Array.isArray(children) ? children.map(this.generatePanel) : [this.generatePanel(children, 0)];
    }

    render() {
        const {
            className: customClassName
        } = this.props;
        const panels = this.generatePanels();
        const className = classnames(
            classes.collapsibleGroup,
            customClassName
        );

        return (
            <div className={className}>
                { panels }
            </div>
        );
    }
}

CollapsibleGroup.displayName = 'CollapsibleGroup';

CollapsibleGroup.propTypes = {
    /**
     * The collapsible panels you wish to group together.
     */
    children: PropTypes.arrayOf(Collapsible).isRequired,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string,
    /**
     * The default expanded panel's index.
     */
    defaultExpandedPanel: PropTypes.number
};

Object.assign(CollapsibleGroup, {
    Collapsible
});

export default CollapsibleGroup;
